import type { AgentSkillSummaryConsumer } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AgentSkillCategory,
    AgentSkillLoadMode,
} from "@common/types/agentSkill";

// Consumers without a label are not shown; agent chat is behind a feature flag.
export const AGENT_SKILL_CONSUMER_LABELS: Partial<
    Record<AgentSkillSummaryConsumer, string>
> = {
    close_run: "Agent",
    mcp: "MCP",
};

export const AVAILABLE_IN_LABEL = "Available in";

/** The labelled consumers of a skill, in schema order; empty without a summary. */
export const agentSkillConsumerLabels = (
    consumers: AgentSkillSummaryConsumer[] | undefined,
): string[] =>
    (consumers ?? []).flatMap((consumer) => {
        const label = AGENT_SKILL_CONSUMER_LABELS[consumer];
        return label === undefined ? [] : [label];
    });

export const AGENT_SKILL_CATEGORY_LABELS: Record<AgentSkillCategory, string> = {
    [AgentSkillCategory.BOOKKEEPING]: "Bookkeeping",
    [AgentSkillCategory.TAX]: "Tax",
    [AgentSkillCategory.COMMUNICATIONS]: "Communications",
    [AgentSkillCategory.CLOSE]: "Close",
    [AgentSkillCategory.OTHER]: "Other",
};

export const AGENT_SKILL_LOAD_MODE_LABELS: Record<AgentSkillLoadMode, string> =
    {
        [AgentSkillLoadMode.ALWAYS]: "Always",
        [AgentSkillLoadMode.ON_DEMAND]: "On demand",
    };

export const AGENT_SKILL_LOAD_MODE_DESCRIPTIONS: Record<
    AgentSkillLoadMode,
    string
> = {
    [AgentSkillLoadMode.ALWAYS]: "In every agent run prompt",
    [AgentSkillLoadMode.ON_DEMAND]: "Listed; the agent loads it when relevant",
};

export const AGENT_SKILL_MANAGE_FORBIDDEN_TOOLTIP =
    "Only firm managers, admins and owners can manage skills";

export const FIRM_SCOPE_LABEL = "Firm brain";

export const SAVE_FAILURE_MESSAGE = "Couldn't save the skill. Try again.";

export const KICK_DEFAULT_LABEL = "Kick default";
export const CUSTOMIZED_LABEL = "Customized";
export const KICK_AUTHOR_LABEL = "Kick";
export const KICK_DEFAULT_OUTDATED_LABEL = "Kick updated this skill";
export const KICK_DEFAULT_OUTDATED_HINT =
    "Kick changed its default since your edit. Reset to default to use the new text; your edits stay in version history.";
export const RESET_TO_DEFAULT_LABEL = "Reset to default";
export const VIEW_KICK_DEFAULT_LABEL = "View Kick's default";
export const RESET_CONFIRMATION_QUESTION =
    "Your edits stay in version history.";
export const PREVIOUS_EDIT_LABEL = "Previous edit";
export const SUMMARY_PENDING_MESSAGE = "Summary appears after the next update";
export const SCREEN_UNAVAILABLE_MESSAGE =
    "Couldn't check the skill text. Try again.";
export const FLAGGED_SAVE_MESSAGE = "Skill text can't be saved";
export const USE_AS_DRAFT_LABEL = "Use as draft";
export const TOOL_NOT_ATTACHED_HINT =
    "Named in the text but not attached by this skill";

export const FLAGGED_FINDING_LABELS: Record<string, string> = {
    unknown_tool: "Names a tool that doesn't exist",
    unknown_skill: "Names a skill that doesn't exist",
    hidden_chars: "Hidden characters",
    html_comment: "Hidden comment",
    role_marker: "Chat role marker",
    prompt_section_heading: "Heading that copies a Kick prompt section",
    override_phrase: "Instruction that overrides Kick's rules",
    encoded_blob: "Encoded data",
    host_tag: "Prompt closing tag",
    authority_override: "Ranks itself above Kick's rules",
    boundary_widening: "Widens what the agent may do",
    exfiltration: "Sends data outside the firm's flow",
    tool_or_gate_bypass: "Skips a preview, approval or write gate",
    persona_change: "Changes the agent's identity",
    cross_workspace: "Reaches outside the workspace",
};

export const METADATA_LOCKED_HINT =
    "Save or cancel your text changes to update category or frequency";
export const SKILL_CHANGED_HINT =
    "This skill changed. Reload to see the latest version";
